import React, {
  forwardRef,
  FunctionComponent,
  useCallback,
  useRef,
  useState,
} from 'react'
import { ApiProduct } from '@vend/types'
import {
  Autocomplete,
  Icon,
  PopoverListFooter,
  PopoverListFooterAction,
} from '@lightspeed/design-system-react'
import { t } from '@transifex/native'
import { Currency } from '@vend/business-components'
import { ProductBadge } from '@vend/react-utilities'
import { fetchApi } from '@vend/utilities'
import { T, UT } from '@transifex/react'
import {
  useRetailerSettingsManager,
  useSaleTransformer,
} from '../../ng-compat/hooks'
import { ProductDetails } from '../AddProductToSale/useAddProductToSale'

export interface WrReactProductSearchProps {
  onProductSelected: (
    product: ProductDetails | null,
    inputValue: string
  ) => void
  onAddNewProduct?: (searchTerm: string) => void
  feelingLucky?: (
    query: string,
    suggestionsAvailable: boolean
  ) => Promise<{ handled: boolean; product: ApiProduct | undefined }>
  ref?: React.ForwardedRef<Autocomplete<HydratedSuggestion>>
}

export interface HydratedSuggestion {
  type: string
  id: string
  name: string
  product: ApiProduct
}

const SUGGEST_URL = '/api/2.0/suggest?type=product&type=variant&page_size=30'

const isHydratedSuggestion = (
  suggestion: HydratedSuggestion | undefined
): suggestion is HydratedSuggestion => Boolean(suggestion)

const isExactMatch = (suggestion: HydratedSuggestion, query: string) => {
  return suggestion.name.toLowerCase() === query.toLowerCase()
}

const productSuggestionToDetails = (suggestion: HydratedSuggestion) => ({
  label: suggestion.name,
  productId: suggestion.id,
  isChild: suggestion.type === 'variant',
})

const getProductForBadge = (suggestion: HydratedSuggestion) => ({
  sku: suggestion.product.sku,
  name: suggestion.name,
  hasVariants: suggestion.product.has_variants,
  variantParentID: suggestion.product.variant_parent_id,
  variantName: suggestion.product.variant_name,
  isComposite: suggestion.product.is_composite,
  variantCount: suggestion.product.variant_count,
  imageThumbnailURL: suggestion.product.image_thumbnail_url,
  skuImageThumbnailURL: suggestion.product.skuImages?.[0]?.sizes.thumb,
})

const ProductSuggestion: FunctionComponent<{
  suggestion: HydratedSuggestion
}> = ({ suggestion }) => {
  const retailer = useRetailerSettingsManager().get()
  return (
    <div className="vd-flex vd-flex--row vd-flex vd-flex--justify-between">
      <ProductBadge
        product={getProductForBadge(suggestion)}
        variant={suggestion.type === 'variant'}
      />
      <div className="vd-align-right">
        <Currency
          value={
            retailer.tax_exclusive
              ? suggestion.product.price_excluding_tax
              : suggestion.product.price_including_tax
          }
        />
      </div>
    </div>
  )
}

const NetworkError: FunctionComponent = () => (
  <div
    data-testid="network-error-message"
    className="vd-popover-list-empty vd-align-center"
  >
    <p className="vd-p">
      <UT
        _str="We’re currently unable to search your&nbsp;products.<br>
  You can still add products to the sale using the barcode&nbsp;or&nbsp;SKU."
        _context="Error message displayed when we can't search for products"
        _inline
      />
    </p>
  </div>
)

export const WrReactProductSearch = forwardRef<
  Autocomplete<HydratedSuggestion>,
  WrReactProductSearchProps
>(({ feelingLucky, onProductSelected, onAddNewProduct }, forwardedRef) => {
  const saleTransformer = useSaleTransformer()
  const hasSuggestions = useRef(false)
  const feelingLuckyHasHandled = useRef(false)
  const [currentQuery, setCurrentQuery] = useState('')
  const [exactMatch, setExactMatch] = useState(false)
  const [networkError, setNetworkError] = useState(false)

  const fetchSuggestions = useCallback(
    async (inputValue: string): Promise<HydratedSuggestion[]> => {
      setCurrentQuery(inputValue)
      setNetworkError(false)
      let responseData: {
        data: Array<{ id: string; type: string }>
      }
      try {
        const response = await fetchApi(`${SUGGEST_URL}&q=${inputValue}`)
        responseData = await response.json()
      } catch (e) {
        setNetworkError(true)
        return []
      }
      const resultList = responseData.data
      const hydratedList: Array<
        HydratedSuggestion | undefined
      > = await Promise.all(
        resultList.map(productSuggestion =>
          saleTransformer.hydrateProduct(productSuggestion.id).then(product =>
            product
              ? {
                  ...productSuggestion,
                  product,
                  name:
                    productSuggestion.type === 'variant'
                      ? product.variant_name
                      : product.name,
                }
              : undefined
          )
        )
      )
      const filteredList = hydratedList.filter(isHydratedSuggestion)
      hasSuggestions.current = Boolean(filteredList.length)
      setExactMatch(
        filteredList.some(suggestion => isExactMatch(suggestion, inputValue))
      )
      return filteredList
    },
    [saleTransformer]
  )

  const onChangeHandler = useCallback(
    (suggestion: HydratedSuggestion | null, inputValue: string) => {
      setCurrentQuery(suggestion ? suggestion.name : '')
      if (feelingLuckyHasHandled.current) {
        feelingLuckyHasHandled.current = false
        return
      }
      setExactMatch(false)
      onProductSelected(
        suggestion ? productSuggestionToDetails(suggestion) : null,
        inputValue
      )
    },
    [onProductSelected]
  )
  const feelingLuckyHandler = useCallback(
    async (inputValue: string) => {
      if (!feelingLucky) {
        return
      }

      const result = await feelingLucky(inputValue, hasSuggestions.current)
      if (result.handled) {
        feelingLuckyHasHandled.current = true
      }
      if (!result.product) {
        return
      }

      return {
        product: result.product,
        id: result.product.id,
        name: result.product.name,
        type:
          result.product.variant_count || result.product.variant_parent_id
            ? 'variant'
            : 'product',
      }
    },
    [feelingLucky]
  )

  return (
    <Autocomplete
      selectedValue={currentQuery}
      fetchSuggestions={fetchSuggestions}
      feelingLucky={feelingLuckyHandler}
      onChange={onChangeHandler}
      renderSuggestion={suggestion => (
        <ProductSuggestion suggestion={suggestion} />
      )}
      leftIcon="fa fa-search"
      ref={forwardedRef}
      placeholder={t('Start typing or scanning', {
        _context: 'Placeholder for product search',
      })}
      renderError={networkError ? <NetworkError /> : null}
      testid="product-search"
      actionBar={
        exactMatch || networkError
          ? null
          : ({ close }) => (
              <PopoverListFooter>
                <PopoverListFooterAction
                  onClick={() => {
                    close()
                    if (onAddNewProduct) {
                      onAddNewProduct(currentQuery)
                    }
                  }}
                >
                  <Icon icon="fa-plus" className="vd-mr1" />
                  <T
                    _str="Add “{searchTerm}” as a new product."
                    _context="Button"
                    searchTerm={currentQuery}
                  />
                </PopoverListFooterAction>
              </PopoverListFooter>
            )
      }
    />
  )
})
